import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowLeft, Plus, Trash2, Image as ImageIcon, Save, X } from "lucide-react";
import {
  defaultBillingTemplate,
  getBillingSettings,
  updateBillingSettings,
  computeTotals,
  CURRENCIES,
} from "../../services/invoiceService";
import { getProject } from "../../services/projectService";
import { useAuthedImage } from "../../hooks/useAuthedImage";

const field = { fontSize: 12.5, border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 10px", outline: "none", width: "100%" };
const smallField = { ...field, width: 90, flexShrink: 0 };
const label = { fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 5 };

const Card = ({ title, children, extra }) => (
  <div style={{ background: "#fff", border: "1px solid var(--borderColor)", borderRadius: 14, boxShadow: "0 1px 3px rgba(15,23,42,.05)", overflow: "hidden", marginBottom: 16 }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px", borderBottom: "1px solid var(--borderColor)", background: "#fafbfc" }}>
      <h6 style={{ margin: 0, fontWeight: 700, fontSize: 13, color: "#0f172a" }}>{title}</h6>
      {extra}
    </div>
    <div style={{ padding: 18 }}>{children}</div>
  </div>
);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const BillingSettings = () => {
  const { id: projectPid } = useParams();
  const navigate = useNavigate();
  const fileInputRef = React.useRef(null);

  const [template, setTemplate] = useState(defaultBillingTemplate());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const logoSrc = useAuthedImage(template.theme?.logoUrl);

  useEffect(() => {
    if (!projectPid) return;
    setLoading(true);
    getBillingSettings(projectPid)
      .then((res) => {
        const s = res.data?.response;
        if (s) setTemplate((t) => ({ ...t, ...s }));
      })
      .catch((err) => toast.error(err?.response?.data?.message || "Failed to load billing settings"))
      .finally(() => setLoading(false));
  }, [projectPid]);

  useEffect(() => {
    if (!projectPid) return;
    getProject(projectPid)
      .then((res) => {
        const p = res.data?.response ?? res.data;
        if (p) setTemplate((t) => (t.from.name ? t : { ...t, from: { ...t.from, name: p.project_name || t.from.name } }));
      })
      .catch(() => {});
  }, [projectPid]);

  const update = (patch) => setTemplate((t) => ({ ...t, ...patch }));
  const updateSection = (section, patch) => setTemplate((t) => ({ ...t, [section]: { ...t[section], ...patch } }));
  const updateRow = (section, idx, patch) =>
    setTemplate((t) => ({ ...t, [section]: t[section].map((r, i) => (i === idx ? { ...r, ...patch } : r)) }));
  const addRow = (section, row) => setTemplate((t) => ({ ...t, [section]: [...t[section], row] }));
  const removeRow = (section, idx) => setTemplate((t) => ({ ...t, [section]: t[section].filter((_, i) => i !== idx) }));

  const updateItem = (idx, patch) =>
    setTemplate((t) => ({ ...t, items: t.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) }));
  const addItem = () => setTemplate((t) => ({ ...t, items: [...t.items, { description: "", type: "text", multiplier: 1 }] }));
  const removeItem = (idx) => setTemplate((t) => ({ ...t, items: t.items.filter((_, i) => i !== idx) }));

  const updateFixedCharge = (idx, patch) =>
    setTemplate((t) => ({ ...t, fixedCharges: t.fixedCharges.map((c, i) => (i === idx ? { ...c, ...patch } : c)) }));
  const addFixedCharge = () => setTemplate((t) => ({ ...t, fixedCharges: [...t.fixedCharges, { description: "", quantity: 1, rate: 0 }] }));
  const removeFixedCharge = (idx) => setTemplate((t) => ({ ...t, fixedCharges: t.fixedCharges.filter((_, i) => i !== idx) }));

  const addBillingEmail = () => {
    const email = emailInput.trim();
    if (!email || !EMAIL_RE.test(email)) {
      toast.error("Enter a valid email address");
      return;
    }
    if (template.billingEmails.includes(email)) {
      setEmailInput("");
      return;
    }
    setTemplate((t) => ({ ...t, billingEmails: [...t.billingEmails, email] }));
    setEmailInput("");
  };
  const removeBillingEmail = (email) =>
    setTemplate((t) => ({ ...t, billingEmails: t.billingEmails.filter((e) => e !== email) }));

  const onCurrencyChange = (code) => {
    const c = CURRENCIES.find((c) => c.code === code) || CURRENCIES[0];
    update({ currency: c.code, currencySymbol: c.symbol });
  };

  const onLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => updateSection("theme", { logoUrl: reader.result });
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await updateBillingSettings(projectPid, template);
      if (res.data?.response) setTemplate((t) => ({ ...t, ...res.data.response }));
      toast.success("Billing settings saved successfully");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save billing settings");
    } finally {
      setSaving(false);
    }
  };

  const totals = computeTotals({ ...template, items: template.fixedCharges });
  const accent = template.theme?.accentColor || "#2563eb";

  return (
    <main className="app-wrapper url-list-page">
      <div className="container-fluid">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" style={{ borderRadius: 8 }}
              onClick={() => navigate(`/project-details/${projectPid}/invoices`)}>
              <ArrowLeft size={14} /> Invoices
            </button>
            <div>
              <h5 style={{ margin: 0, fontWeight: 800, color: "#0f172a" }}>Billing Settings</h5>
              <p style={{ margin: 0, fontSize: 12, color: "var(--muteColor)" }}>Reusable template applied to every invoice generated for this project</p>
            </div>
          </div>
          <button className="btn btn-primary btn-sm d-flex align-items-center gap-2" style={{ borderRadius: 8 }} onClick={handleSave} disabled={saving || loading}>
            <Save size={14} /> {saving ? "Saving…" : "Save Template"}
          </button>
        </div>

        <div className="row g-3">
          {/* ── Editor column ── */}
          <div className="col-12 col-xl-6">
            <Card title="Branding & Currency">
              <div className="row g-3">
                <div className="col-6">
                  <label style={label}>Accent Color</label>
                  <input type="color" value={accent} onChange={(e) => updateSection("theme", { accentColor: e.target.value })} style={{ width: "100%", height: 36, border: "1px solid #e2e8f0", borderRadius: 8, padding: 2 }} />
                </div>
                <div className="col-6">
                  <label style={label}>Currency</label>
                  <select style={field} value={template.currency} onChange={(e) => onCurrencyChange(e.target.value)}>
                    {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code} ({c.symbol})</option>)}
                  </select>
                </div>
                <div className="col-12">
                  <label style={label}>Logo</label>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {logoSrc && <img src={logoSrc} alt="logo" style={{ height: 40, borderRadius: 6, border: "1px solid #e2e8f0" }} />}
                    <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2" style={{ borderRadius: 8 }} onClick={() => fileInputRef.current?.click()}>
                      <ImageIcon size={13} /> Upload Logo
                    </button>
                    <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={onLogoUpload} />
                    {template.theme?.logoUrl && (
                      <button type="button" className="btn btn-outline-danger btn-sm" style={{ borderRadius: 8 }} onClick={() => updateSection("theme", { logoUrl: "" })}>Remove</button>
                    )}
                  </div>
                </div>
              </div>
            </Card>

            <Card title="From (Your Business)">
              <div className="row g-2">
                <div className="col-12"><label style={label}>Name</label><input style={field} value={template.from.name} onChange={(e) => updateSection("from", { name: e.target.value })} /></div>
                <div className="col-12"><label style={label}>Address & Details</label><textarea rows={2} style={field} value={template.from.details} onChange={(e) => updateSection("from", { details: e.target.value })} /></div>
              </div>
            </Card>

            <Card title="Bill To (Client)">
              <div className="row g-2">
                <div className="col-12"><label style={label}>Name</label><input style={field} value={template.to.name} onChange={(e) => updateSection("to", { name: e.target.value })} /></div>
                <div className="col-12"><label style={label}>Address & Details</label><textarea rows={2} style={field} value={template.to.details} onChange={(e) => updateSection("to", { details: e.target.value })} /></div>
              </div>
            </Card>

            <Card title="Billing Emails">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: template.billingEmails.length ? 12 : 0 }}>
                {template.billingEmails.map((email) => (
                  <span key={email} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: "#334155", background: "#f1f5f9", borderRadius: 999, padding: "5px 6px 5px 12px" }}>
                    {email}
                    <button type="button" onClick={() => removeBillingEmail(email)} style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b", display: "flex", alignItems: "center", padding: 2, borderRadius: "50%" }}>
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  type="email"
                  style={{ ...field, flex: 1 }}
                  placeholder="e.g. accounts@clientco.com"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addBillingEmail(); } }}
                />
                <button type="button" className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1" style={{ borderRadius: 7, flexShrink: 0 }} onClick={addBillingEmail}>
                  <Plus size={13} /> Add
                </button>
              </div>
              <p style={{ margin: "8px 0 0", fontSize: 11, color: "#94a3b8" }}>Default recipients when sending an invoice for this project. These are never printed on the invoice itself.</p>
            </Card>

            <Card title="Line Items" extra={
              <button type="button" className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1" style={{ borderRadius: 7 }} onClick={addItem}>
                <Plus size={13} /> Add Item
              </button>
            }>
              {template.items.map((it, i) => (
                <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                  <input style={{ ...field, flex: 1 }} placeholder="Description" value={it.description} onChange={(e) => updateItem(i, { description: e.target.value })} />
                  <select style={smallField} value={it.type} onChange={(e) => updateItem(i, { type: e.target.value })}>
                    <option value="text">Text</option>
                    <option value="voice">Voice</option>
                  </select>
                  <input type="number" min="0" step="0.01" style={smallField} placeholder="Multiplier" value={it.multiplier} onChange={(e) => updateItem(i, { multiplier: e.target.value })} />
                  <button type="button" className="btn btn-sm btn-outline-danger" style={{ borderRadius: 7, flexShrink: 0 }} onClick={() => removeItem(i)} disabled={template.items.length === 1}>
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
              <p style={{ margin: "4px 0 0", fontSize: 11, color: "#94a3b8" }}>These items pre-fill every new invoice for this project — edit them per invoice afterward as needed.</p>
            </Card>

            <Card title="Fixed Charges" extra={
              <button type="button" className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1" style={{ borderRadius: 7 }} onClick={addFixedCharge}>
                <Plus size={13} /> Add Charge
              </button>
            }>
              {template.fixedCharges.map((c, i) => (
                <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                  <input style={{ ...field, flex: 1 }} placeholder="Description" value={c.description} onChange={(e) => updateFixedCharge(i, { description: e.target.value })} />
                  <input type="number" min="0" style={smallField} placeholder="Qty" value={c.quantity} onChange={(e) => updateFixedCharge(i, { quantity: e.target.value })} />
                  <input type="number" min="0" style={smallField} placeholder="Rate" value={c.rate} onChange={(e) => updateFixedCharge(i, { rate: e.target.value })} />
                  <button type="button" className="btn btn-sm btn-outline-danger" style={{ borderRadius: 7, flexShrink: 0 }} onClick={() => removeFixedCharge(i)} disabled={template.fixedCharges.length === 1}>
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
              <p style={{ margin: "4px 0 0", fontSize: 11, color: "#94a3b8" }}>Applied by default to every invoice for this project — e.g. service fees. Editable per invoice afterward.</p>
            </Card>

            <Card title="Document Title & Invoice Numbering">
              <div className="row g-2">
                <div className="col-12"><label style={label}>Document Title</label><input style={field} value={template.documentTitle} onChange={(e) => update({ documentTitle: e.target.value })} /></div>
                <div className="col-12">
                  <label style={label}>Invoice Number Prefix</label>
                  <input style={field} placeholder="e.g. INV-" value={template.invoiceNumberPrefix} onChange={(e) => update({ invoiceNumberPrefix: e.target.value })} />
                  <p style={{ margin: "4px 0 0", fontSize: 11, color: "#94a3b8" }}>New invoices are auto-numbered as {template.invoiceNumberPrefix || "INV-"}0001, {template.invoiceNumberPrefix || "INV-"}0002, and so on.</p>
                </div>
                <div className="col-12">
                  <label style={label}>Payment Due (days after issue)</label>
                  <input type="number" min="0" max="365" style={{ ...field, width: 120 }} placeholder="e.g. 15" value={template.dueDays} onChange={(e) => update({ dueDays: e.target.value })} />
                  <p style={{ margin: "4px 0 0", fontSize: 11, color: "#94a3b8" }}>Used to auto-fill the due date on new invoices when no due date is set manually (e.g. Net 15, Net 30).</p>
                </div>
              </div>
            </Card>

            <Card title="Taxes" extra={
              <button type="button" className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1" style={{ borderRadius: 7 }}
                onClick={() => addRow("taxes", { label: "", type: "percent", value: 0 })}>
                <Plus size={13} /> Add Tax
              </button>
            }>
              {template.taxes.map((t, i) => (
                <div key={i} className="row g-2 align-items-center" style={{ marginBottom: 8 }}>
                  <div className="col-5"><input style={field} placeholder="e.g. CGST" value={t.label} onChange={(e) => updateRow("taxes", i, { label: e.target.value })} /></div>
                  <div className="col-3"><input type="number" style={field} value={t.value} onChange={(e) => updateRow("taxes", i, { value: e.target.value })} /></div>
                  <div className="col-3">
                    <select style={field} value={t.type} onChange={(e) => updateRow("taxes", i, { type: e.target.value })}>
                      <option value="percent">%</option>
                      <option value="flat">Flat</option>
                    </select>
                  </div>
                  <div className="col-1">
                    <button type="button" className="btn btn-sm btn-outline-danger" style={{ borderRadius: 7 }} onClick={() => removeRow("taxes", i)}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </Card>

            <Card title="Discounts" extra={
              <button type="button" className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1" style={{ borderRadius: 7 }}
                onClick={() => addRow("discounts", { label: "", type: "percent", value: 0 })}>
                <Plus size={13} /> Add Discount
              </button>
            }>
              {template.discounts.map((d, i) => (
                <div key={i} className="row g-2 align-items-center" style={{ marginBottom: 8 }}>
                  <div className="col-5"><input style={field} placeholder="e.g. Early bird" value={d.label} onChange={(e) => updateRow("discounts", i, { label: e.target.value })} /></div>
                  <div className="col-3"><input type="number" style={field} value={d.value} onChange={(e) => updateRow("discounts", i, { value: e.target.value })} /></div>
                  <div className="col-3">
                    <select style={field} value={d.type} onChange={(e) => updateRow("discounts", i, { type: e.target.value })}>
                      <option value="percent">%</option>
                      <option value="flat">Flat</option>
                    </select>
                  </div>
                  <div className="col-1">
                    <button type="button" className="btn btn-sm btn-outline-danger" style={{ borderRadius: 7 }} onClick={() => removeRow("discounts", i)}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </Card>

            {template.customFields.map((f, i) => (
              <Card key={i} title={f.label || "Custom Field"} extra={
                <button type="button" className="btn btn-sm btn-outline-danger" style={{ borderRadius: 7 }} onClick={() => removeRow("customFields", i)}>
                  <Trash2 size={13} />
                </button>
              }>
                <div className="row g-2">
                  <div className="col-12"><label style={label}>Label</label><input style={field} placeholder="e.g. About Us" value={f.label} onChange={(e) => updateRow("customFields", i, { label: e.target.value })} /></div>
                  <div className="col-12"><label style={label}>Value</label><textarea rows={2} style={field} value={f.value} onChange={(e) => updateRow("customFields", i, { value: e.target.value })} /></div>
                </div>
              </Card>
            ))}
            <button type="button" className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1 mb-4" style={{ borderRadius: 7 }}
              onClick={() => addRow("customFields", { label: "", value: "" })}>
              <Plus size={13} /> Add Field
            </button>
          </div>

          {/* ── Preview column ── */}
          <div className="col-12 col-xl-6" style={{ alignSelf: "flex-start", position: "sticky", top: 85 }}>
            <p style={{ fontSize: 12, color: "var(--muteColor)", marginBottom: 8 }}>
              Real invoice number and dates are filled in when an invoice is generated — everything else, including these items, pre-fills from this template.
            </p>
            <div style={{ background: "#fff", border: "1px solid var(--borderColor)", borderRadius: 14, boxShadow: "0 1px 3px rgba(15,23,42,.05)", padding: 32, fontFamily: "Arial, sans-serif", color: "#1e293b", maxWidth: "100%", overflow: "hidden", boxSizing: "border-box" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 28 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  {logoSrc && (
                    <img src={logoSrc} alt="logo" style={{ height: 48, marginBottom: 10 }} />
                  )}
                  <p style={{ margin: "0 0 6px", fontSize: 13, fontWeight: 700, color: "#1e293b" }}>{template.from.name || "Your Business"}</p>
                  <p style={{ margin: 0, fontSize: 12, whiteSpace: "pre-line", color: "#475569", overflowWrap: "anywhere" }}>{template.from.details}</p>
                </div>
                <div style={{ textAlign: "right", minWidth: 0, flexShrink: 0 }}>
                  <h2 style={{ margin: 0, fontWeight: 900, letterSpacing: 1, color: accent }}>{template.documentTitle || "INVOICE"}</h2>
                  <p style={{ margin: "4px 0 0", fontSize: 11, color: "#64748b" }}>INV-0001</p>
                  <p style={{ margin: "2px 0 0", fontSize: 11, color: "#64748b" }}>Issued: —</p>
                  <p style={{ margin: "2px 0 0", fontSize: 11, color: "#64748b" }}>Due: —</p>
                  <p style={{ margin: "8px 0 0", fontSize: 14, fontWeight: 800, color: accent }}>Total: {template.currencySymbol}{totals.total.toFixed(2)}</p>
                </div>
              </div>

              <div style={{ marginBottom: 22 }}>
                <p style={{ margin: "0 0 4px", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase" }}>Bill To</p>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>{template.to.name || "Client Name"}</p>
                <p style={{ margin: 0, fontSize: 12, whiteSpace: "pre-line", color: "#475569", overflowWrap: "anywhere" }}>{template.to.details}</p>
              </div>

              <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse", marginBottom: 18 }}>
                <thead>
                  <tr style={{ background: accent }}>
                    <th style={{ textAlign: "left", padding: "8px 10px", fontSize: 11, color: "#fff", fontWeight: 700 }}>Description</th>
                    <th style={{ textAlign: "right", padding: "8px 10px", fontSize: 11, color: "#fff", fontWeight: 700, width: 60 }}>Qty</th>
                    <th style={{ textAlign: "right", padding: "8px 10px", fontSize: 11, color: "#fff", fontWeight: 700, width: 90 }}>Rate</th>
                    <th style={{ textAlign: "right", padding: "8px 10px", fontSize: 11, color: "#fff", fontWeight: 700, width: 90 }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {template.fixedCharges.map((c, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "8px 10px", fontSize: 12, overflowWrap: "anywhere" }}>{c.description || "—"}</td>
                      <td style={{ padding: "8px 10px", fontSize: 12, textAlign: "right" }}>{c.quantity || 0}</td>
                      <td style={{ padding: "8px 10px", fontSize: 12, textAlign: "right" }}>{template.currencySymbol}{(Number(c.rate) || 0).toFixed(2)}</td>
                      <td style={{ padding: "8px 10px", fontSize: 12, textAlign: "right", fontWeight: 600 }}>{template.currencySymbol}{((Number(c.quantity) || 0) * (Number(c.rate) || 0)).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 22 }}>
                <div style={{ width: 240 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "4px 0", color: "#475569" }}>
                    <span>Subtotal</span><span>{template.currencySymbol}{totals.subtotal.toFixed(2)}</span>
                  </div>
                  {totals.taxLines.map((t, i) => (
                    <div key={`tax-${i}`} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "4px 0", color: "#475569" }}>
                      <span>{t.label || "Tax"} {t.type === "percent" ? `(${t.value || 0}%)` : ""}</span><span>{template.currencySymbol}{t.amount.toFixed(2)}</span>
                    </div>
                  ))}
                  {totals.discountLines.map((d, i) => (
                    <div key={`disc-${i}`} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "4px 0", color: "#475569" }}>
                      <span>{d.label || "Discount"} {d.type === "percent" ? `(${d.value || 0}%)` : ""}</span><span>-{template.currencySymbol}{d.amount.toFixed(2)}</span>
                    </div>
                  ))}
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 15, fontWeight: 800, padding: "8px 0 0", marginTop: 6, borderTop: `2px solid ${accent}`, color: accent }}>
                    <span>Total</span><span>{template.currencySymbol}{totals.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {template.customFields.filter((f) => f.value).map((f, i) => (
                <div key={i} style={{ marginBottom: 12 }}>
                  <p style={{ margin: "0 0 3px", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase" }}>{f.label || "Note"}</p>
                  <p style={{ margin: 0, fontSize: 12, color: "#475569", whiteSpace: "pre-line", overflowWrap: "anywhere" }}>{f.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default BillingSettings;
